<template>
  <main class="login-page">
    <section class="login-shell" aria-label="Acesso ao sistema BMS Gestão">
      <div class="login-panel">
        <form
          v-if="sectionsEnable.login"
          class="login-form"
          @submit.prevent="sendLogin"
        >
          <img :src="bmsGestaoLogo" class="brand-logo" alt="BMS Gestão" />

          <div class="login-heading">
            <h1>Bem-vindo de volta!</h1>
            <p>Faça login para acessar o sistema BMS Gestão.</p>
          </div>

          <div class="form-stack">
            <label class="field-label" for="login-user">Usuário</label>
            <div class="input-field">
              <b-icon-person class="field-icon" aria-hidden="true" />
              <input
                id="login-user"
                type="text"
                name="email"
                autocomplete="username"
                placeholder="Digite seu usuário"
                v-model="login.email"
              />
            </div>

            <label class="field-label" for="login-password">Senha</label>
            <div class="input-field password-field">
              <b-icon-lock class="field-icon" aria-hidden="true" />
              <input
                id="login-password"
                :type="showPassword ? 'text' : 'password'"
                name="pass"
                autocomplete="current-password"
                placeholder="Digite sua senha"
                v-model="login.password"
              />
              <button
                class="toggle-password"
                type="button"
                :aria-label="showPassword ? 'Ocultar senha' : 'Mostrar senha'"
                @click="togglePasswordVisibility"
              >
                <b-icon-eye-slash v-if="showPassword" aria-hidden="true" />
                <b-icon-eye v-else aria-hidden="true" />
              </button>
            </div>
          </div>

          <div class="login-options">
            <label class="remember-access" for="remember-access">
              <input
                id="remember-access"
                type="checkbox"
                v-model="rememberAccess"
                :disabled="isLoggingIn"
                @change="handleRememberAccessChange"
              />
              <span>Lembrar meu acesso</span>
            </label>

            <button
              class="forgot-link"
              type="button"
              @click="handleSectionsEnable('requestRecoveryPassword')"
            >
              Esqueceu a senha?
            </button>
          </div>

          <button
            class="primary-action"
            type="submit"
            :disabled="isLoggingIn"
            :aria-busy="isLoggingIn ? 'true' : 'false'"
          >
            <b-spinner v-if="isLoggingIn" small aria-hidden="true" />
            <span>{{ isLoggingIn ? "Entrando..." : "Entrar" }}</span>
            <b-icon-arrow-right v-if="!isLoggingIn" aria-hidden="true" />
          </button>

          <footer class="login-footer">
            <img :src="softwareLogo" alt="BMS Softwares" />
            <span></span>
            <p>Tecnologia que transforma gestão em resultados.</p>
          </footer>
        </form>

        <form
          v-if="sectionsEnable.requestRecoveryPassword"
          class="login-form recovery-form"
          @submit.prevent="forgotMyPassword"
        >
          <img :src="bmsGestaoLogo" class="brand-logo" alt="BMS Gestão" />

          <div class="login-heading">
            <h1>Recuperar senha</h1>
            <p>Enviaremos instruções de redefinição para seu email.</p>
          </div>

          <div class="form-stack">
            <label class="field-label" for="recovery-email">E-mail</label>
            <div
              :class="`input-field ${
                invalidinputEmailToResetPassword ? 'invalid-input' : ''
              }`"
            >
              <b-icon-envelope class="field-icon" aria-hidden="true" />
              <input
                id="recovery-email"
                ref="emailToResetPassword"
                type="email"
                name="email"
                autocomplete="email"
                placeholder="Digite seu e-mail"
                v-model="emailToResetPassword"
                @input="handleClearInvalidMessage"
              />
            </div>
            <p v-if="invalidMessage" class="invalid-message">
              {{ invalidMessage }}
            </p>
          </div>

          <button
            class="primary-action"
            type="submit"
            :disabled="spinResetPassword"
          >
            <span>{{ spinResetPassword ? "Enviando..." : "Recuperar senha" }}</span>
            <b-spinner v-if="spinResetPassword" small />
            <b-icon-arrow-right v-else aria-hidden="true" />
          </button>

          <button
            class="back-login"
            type="button"
            @click="handleSectionsEnable('login')"
          >
            <b-icon-arrow-left-short aria-hidden="true" />
            <span>Voltar ao login</span>
          </button>
        </form>

        <div v-if="sectionsEnable.forgotMyPassword" class="recovery-card">
          <ResetPassword @backToLogin="handleSectionsEnable('login')" />
        </div>
      </div>

      <div class="panel-divider" aria-hidden="true">
        <span class="divider-icon">
          <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
            <path
              d="M13.6 3.1c2.6-.8 5.1-.6 6.2-.3.3 1.1.5 3.6-.3 6.2-.8 2.7-2.7 5.1-5.6 7.3l-.6.4-3.8-3.8.4-.6c2.2-2.9 4.6-4.8 7.3-5.6Zm2.8 5.9a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6ZM8.7 14.2l1.9 1.9-2 3.5-1.1-2.5-2.5-1.1 3.7-1.8Zm1.5-5.1c-.8.7-1.6 1.6-2.4 2.6l-3.4-.3 2.9-2.7 2.9.4Zm4.7 4.7.4 2.9-2.7 2.9-.3-3.4c1-.8 1.9-1.6 2.6-2.4Z"
            />
          </svg>
        </span>
      </div>

      <aside class="showcase-panel" aria-label="BMS Gestão">
        <div class="showcase-content">
          <span class="product-badge">BMS GESTÃO</span>
          <div class="showcase-copy">
            <h2>
              Solução completa para
              <strong>gestão eficiente.</strong>
            </h2>
            <p>Mais controle, eficiência e praticidade para o seu negócio.</p>
          </div>

          <div class="rocket-art" aria-hidden="true">
            <div class="glow-orbit"></div>
            <img :src="bmsGestaoIcon" alt="" />
          </div>

          <div class="feature-grid">
            <article class="feature-card">
              <b-icon-graph-up aria-hidden="true" />
              <h3>Performance</h3>
              <p>Acompanhe resultados e aumente eficiência.</p>
            </article>
            <article class="feature-card">
              <b-icon-kanban aria-hidden="true" />
              <h3>Gestão</h3>
              <p>Planejamento e controle na palma da mão.</p>
            </article>
            <article class="feature-card">
              <b-icon-lightning aria-hidden="true" />
              <h3>Inteligência</h3>
              <p>Relatórios inteligentes para decisões assertivas.</p>
            </article>
          </div>
        </div>
      </aside>
    </section>
  </main>
</template>

<script>
import { mapState } from "vuex";
import ResetPassword from "../ResetPassword/ResetPassword.vue";
import LoginService from "../../services/serviceLogin";
import bmsGestaoLogo from "../../assets/bms-gestao-logo.png";
import bmsGestaoIcon from "../../assets/bms-gestao-icon.png";
import softwareLogo from "../../assets/bms-softwares-logo.png";
import ServiceMail from "../../services/serviceMail";
import { clearTokens } from "../../utils/clearTokens";
import { ValidateEmailResetPassword } from "../../validators/emailResetPassword";
import { HandleErrors } from "../../utils/handleErrors";
import api from "../../services/axios";
import moment from "moment";

export default {
  components: {
    ResetPassword,
  },
  data() {
    return {
      bmsGestaoLogo,
      bmsGestaoIcon,
      softwareLogo,
      showPassword: false,
      rememberAccess: false,
      isLoggingIn: false,
      rememberedAccessKey: "bmsGestaoRememberedAccess",
      typeWaveAnimation: "",
      visibilitySaibaMais: "hideLogin",
      visibilityLogin: "showLogin",
      datainiciouso: moment().format("YYYY-MM-DD"),
      isCheck: false,
      isWarning: false,
      hourglass: true,
      infoNewClient: {},
      login: {
        email: "",
        password: "",
      },
      companyTotal: { companyCad: {}, companyLogger: {}, companyMail: {} },
      company: {
        nome: "",
        telefone: "",
        email: "",
        cpfcnpj: "",
        type: 1,
        companyName: "",
        datainiciouso: "",
        datafimuso: "",
      },
      logger: {
        nome: "",
        email: "",
        password: "",
      },
      email: {
        office: "",
        acting: "",
        employee: "",
      },
      sectionsEnable: {
        login: true,
        tester: false,
        forgotMyPassword: false,
        requestRecoveryPassword: false,
      },
      tipo: [
        { value: 1, text: "CPF" },
        { value: 2, text: "CNPJ" },
      ],
      cargo: [
        { value: "", text: "Selecione..." },
        { value: "Analista Contas a Pagar", text: "Analista Contas a Pagar" },
        {
          value: "Analista Contas a Receber",
          text: "Analista Contas a Receber",
        },
        { value: "Analista Faturamento", text: "Analista Faturamento" },
        { value: "Compras/Produção", text: "Compras/Produção" },
        { value: "Contabilidade/Fiscal", text: "Contabilidade/Fiscal" },
        {
          value: "Coordenador/Gerente Financeiro",
          text: "Coordenador/Gerente Financeiro",
        },
        { value: "Diretor Financeiro/CFO", text: "Diretor Financeiro/CFO" },
        { value: "Diretor/Sócio", text: "Diretor/Sócio" },
        { value: "Estagiário/Assistente", text: "Estagiário/Assistente" },
        { value: "TI", text: "TI" },
        { value: "Vendas/Comercial", text: "Vendas/Comercial" },
        { value: "Outros", text: "Outros" },
      ],
      ramo: [
        { value: "", text: "Selecione..." },
        { value: "Alimentação e Bebidas", text: "Alimentação e Bebidas" },
        { value: "Vestuário e calçados", text: "Vestuário e calçados" },
        { value: "Construção", text: "Construção" },
        { value: "Saúde", text: "Saúde" },
        { value: "Educação", text: "Educação" },
        { value: "Serviços pessoais", text: "Serviços pessoais" },
        { value: "Serviços especializados", text: "Serviços especializados" },
        { value: "Informática/Tecnologia", text: "Informática/Tecnologia" },
        { value: "Outro", text: "Outro" },
      ],
      funcionario: [
        { value: "", text: "Selecione..." },
        { value: "1 a 5", text: "De 1 a 5 funcionários" },
        { value: "6 até 15", text: "De 6 até 15 funcionários" },
        { value: "16", text: "Mais de 16 funcionários" },
      ],
      emailToResetPassword: "",
      spinResetPassword: false,
      invalidMessage: "",
      invalidinputEmailToResetPassword: "",
      open: true,
    };
  },
  methods: {
    toggleOpen() {
      this.open = !this.open;
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    loadRememberedAccess() {
      const rememberedAccess = localStorage.getItem(this.rememberedAccessKey);

      if (!rememberedAccess) return;

      this.login.email = rememberedAccess;
      this.rememberAccess = true;
    },
    persistRememberedAccess() {
      if (this.rememberAccess) {
        localStorage.setItem(this.rememberedAccessKey, this.login.email);
        return;
      }

      localStorage.removeItem(this.rememberedAccessKey);
    },
    handleRememberAccessChange() {
      if (!this.rememberAccess) {
        localStorage.removeItem(this.rememberedAccessKey);
      }
    },
    handleSectionsEnable(name) {
      this.sectionsEnable = {
        login: false,
        tester: false,
        forgotMyPassword: false,
        requestRecoveryPassword: false,
        [name]: true,
      };

      if (name !== "login") this.clearAllInputs();
    },
    async sendLogin() {
      try {
        if (this.login.email === "" || this.login.password === "") {
          return this.$toast.open({
            message: "O campo login e senha devem ser preenchidos",
            type: "info",
          });
        }

        this.isLoggingIn = true;
        const { token, permissions, userLevel, isTrackerUser } =
          await LoginService.logar(this.login);

        this.persistRememberedAccess();
        localStorage.setItem("token", token);
        localStorage.setItem("permissions", JSON.stringify(permissions));
        localStorage.setItem("userLevel", userLevel);
        localStorage.setItem("isTrackerUser", isTrackerUser);

        const tokenList = JSON.parse(localStorage.getItem("tokenList"));
        if (tokenList && tokenList.length) {
          tokenList.push({
            token,
            userLevel,
            permissions,
            nameCompany: this.dataCompany.nome,
          });

          localStorage.setItem("tokenList", JSON.stringify(tokenList));
        }

        if (userLevel === "accountant") {
          return this.$router
            .push({
              path: "/area-contador",
            })
            .then(() => {
              location.reload();
            });
        }

        this.$router
          .push({
            path: "/home",
          })
          .then(() => {
            location.reload();
          });
      } catch (error) {
        return this.$toast.open({
          message:
            error?.response?.data?.message ||
            "Verifique se o login e senha estão corretos!",
          type: "info",
        });
      } finally {
        this.isLoggingIn = false;
      }
    },

    async sendMail() {
      await api.post("/mail/info/client", this.infoNewClient);
    },

    async forgotMyPassword() {
      try {
        const isValid = ValidateEmailResetPassword(this.emailToResetPassword);
        if (isValid) {
          return this.handleInputEmailToResetPassword();
        }

        this.spinResetPassword = true;

        await ServiceMail.confirmCode({ email: this.emailToResetPassword });

        this.handleSectionsEnable("login");
        this.emailToResetPassword = "";
        this.$toast.open({
          message: "Email encaminhado",
          type: "success",
        });
      } catch (error) {
        console.log(error);
        this.$toast.open({
          message:
            error?.response?.data?.message ||
            "Ocorreu um erro ao enviar o email",
          type: "info",
        });
      } finally {
        this.spinResetPassword = false;
        this.loadingPassword = "";
      }
    },

    handleClearInvalidMessage() {
      this.clearInvalidInput();
    },

    handleInputEmailToResetPassword() {
      this.$refs.emailToResetPassword.focus();
      this.invalidinputEmailToResetPassword =
        "invalidinputEmailToResetPassword";
      this.invalidMessage = "* Campo Email deve ser um email válido!";
    },

    clearInvalidInput() {
      this.invalidinputEmailToResetPassword = "";
      this.invalidMessage = "";
    },

    clearAllInputs() {
      this.company = {
        nome: "",
        telefone: "",
        email: "",
        cpfcnpj: "",
        type: 1,
        companyName: "",
        datainiciouso: "",
        datafimuso: "",
      };

      this.logger = {
        nome: "",
        email: "",
        password: "",
      };

      this.email = {
        office: "",
        acting: "",
        employee: "",
      };

      this.emailToResetPassword = "";
    },

    verifyExitsToken() {
      if (!this.getToken) {
        const token = localStorage.getItem("token");
        if (token) {
          return this.$router.push({
            path: "/home",
          });
        }

        clearTokens();
      }
    },

    assingValueCadCompany() {
      let currentDate = moment();
      let dateAfter30Days = currentDate.add(30, "days");
      let dateFormatted = dateAfter30Days.format("YYYY-MM-DD");
      this.logger.nome = this.company.nome;
      this.logger.email = this.company.email;
      this.company.datainiciouso = this.datainiciouso;
      this.company.datafimuso = dateFormatted;
      this.companyTotal.companyCad = this.company;
      this.companyTotal.companyLogger = this.logger;
      this.companyTotal.companyMail = this.email;
      this.infoNewClient = this.companyTotal;
    },

    async redirectAndLogger() {
      this.login.email = this.logger.email;
      this.login.password = this.logger.password;
      this.handleSectionsEnable("login");

      this.$bvModal.hide("modal-loader");
      await this.sendLogin();
    },

    async newCompany() {
      try {
        this.$bvModal.show("modal-loader");
        this.assingValueCadCompany();
        await this.sendMail();
        await api.post("/gestao/tester", this.companyTotal);
        this.isCheck = true;
        this.hourglass = false;
        this.redirectAndLogger();
      } catch (error) {
        this.hourglass = false;
        this.isCheck = false;
        this.isWarning = true;
        const typeError = HandleErrors(error?.response?.data?.message);
        if (typeError && Object.keys(typeError).length) {
          return this.$toast.open({
            message: typeError.message,
            type: "info",
          });
        }
        this.$toast.open({
          message:
            error?.response?.data?.message ||
            "Ocorreu um erro ao salvar o novo usuario",
          type: "info",
        });
      }
    },
  },
  computed: {
    ...mapState(["dataCompany"]),
    maskCelular() {
      return "(##) #####-####";
    },
    maskcpfcnpj() {
      if (!this.company.cpfcnpj) {
        return "";
      }

      if (this.company.cpfcnpj.length <= 14) {
        return "###.###.###-##";
      }

      if (this.company.cpfcnpj.length > 14) {
        return "##.###.###/####-##";
      } else {
        return "";
      }
    },

    getToken() {
      return this.$route.query.token;
    },
  },
  mounted() {
    this.verifyExitsToken();
    this.loadRememberedAccess();

    if (this.getToken) {
      this.handleSectionsEnable("forgotMyPassword");
    }
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.login-page {
  align-items: center;
  background:
    linear-gradient(90deg, rgba(90, 61, 173, 0.08) 1px, transparent 1px),
    linear-gradient(180deg, rgba(90, 61, 173, 0.07) 1px, transparent 1px),
    radial-gradient(circle at 12% 18%, rgba(123, 91, 217, 0.2), transparent 30%),
    radial-gradient(circle at 88% 74%, rgba(70, 46, 144, 0.18), transparent 28%),
    #eef2f8;
  background-size: 42px 42px, 42px 42px, auto, auto, auto;
  display: flex;
  font-family: "Poppins", "Montserrat", Arial, sans-serif;
  height: 100vh;
  inset: 0;
  justify-content: center;
  overflow: hidden;
  padding: clamp(10px, 2.2vh, 24px) clamp(12px, 2vw, 28px);
  position: fixed;
  width: 100%;
}

.login-shell {
  aspect-ratio: 8 / 5;
  background: #ffffff;
  border-radius: clamp(14px, 1.4vw, 22px);
  box-shadow: 0 26px 70px rgba(29, 22, 63, 0.22);
  display: flex;
  height: 100%;
  max-height: 900px;
  max-width: min(1440px, 100%);
  min-height: 0;
  overflow: hidden;
  position: relative;
  width: auto;
  z-index: 1;
}

.login-panel,
.showcase-panel {
  flex: 1 1 50%;
  min-width: 0;
}

.login-panel {
  align-items: center;
  background:
    linear-gradient(118deg, rgba(90, 61, 173, 0.075) 0%, transparent 31%),
    radial-gradient(circle at 82% 18%, rgba(90, 61, 173, 0.12), transparent 27%),
    radial-gradient(circle at 18% 84%, rgba(137, 95, 255, 0.11), transparent 30%),
    linear-gradient(145deg, #ffffff 0%, #fdfcff 54%, #f4f0ff 100%);
  display: flex;
  justify-content: center;
  min-height: 0;
  overflow: hidden;
  padding: clamp(18px, 4vh, 46px) clamp(28px, 6vw, 96px);
  position: relative;
}

.login-panel::before {
  background-image:
    radial-gradient(rgba(58, 35, 130, 0.2) 1.2px, transparent 1.2px),
    linear-gradient(135deg, rgba(90, 61, 173, 0.12), transparent 48%);
  background-size: 18px 18px, 100% 100%;
  border-radius: 50%;
  content: "";
  height: clamp(180px, 24vw, 320px);
  left: clamp(-150px, -10vw, -80px);
  opacity: 0.46;
  pointer-events: none;
  position: absolute;
  top: clamp(-120px, -12vh, -70px);
  width: clamp(180px, 24vw, 320px);
}

.login-panel::after {
  background: rgba(90, 61, 173, 0.025);
  border: 1px solid rgba(90, 61, 173, 0.13);
  border-radius: 50%;
  box-shadow:
    inset 0 0 0 44px rgba(90, 61, 173, 0.025),
    0 0 90px rgba(90, 61, 173, 0.09);
  content: "";
  height: clamp(300px, 38vw, 520px);
  pointer-events: none;
  position: absolute;
  right: clamp(-310px, -22vw, -190px);
  top: 50%;
  transform: translateY(-50%);
  width: clamp(300px, 38vw, 520px);
}

.login-form,
.recovery-card {
  max-width: 430px;
  position: relative;
  width: 100%;
  z-index: 1;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: clamp(12px, 1.9vh, 20px);
  max-height: 100%;
}

.brand-logo {
  align-self: center;
  display: block;
  max-width: clamp(245px, 24vw, 390px);
  width: 100%;
}

.login-heading {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.login-heading h1 {
  color: #111827;
  font-size: clamp(18px, 1.45vw, 22px);
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.2;
  margin: 0;
}

.login-heading p {
  color: #566174;
  font-size: clamp(13px, 1vw, 15px);
  line-height: 1.55;
  margin: 0;
}

.form-stack {
  display: flex;
  flex-direction: column;
  gap: clamp(6px, 1vh, 10px);
}

.field-label {
  color: #101828;
  font-size: 14px;
  font-weight: 700;
  margin: 4px 0 0;
}

.input-field {
  align-items: center;
  background: #ffffff;
  border: 1px solid #d5d9e4;
  border-radius: 8px;
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  min-height: clamp(44px, 5.8vh, 56px);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.password-field {
  grid-template-columns: 48px minmax(0, 1fr) 48px;
}

.input-field:focus-within {
  border-color: #6d52cf;
  box-shadow: 0 0 0 4px rgba(109, 82, 207, 0.14);
}

.field-icon {
  color: #4d5871;
  font-size: 20px;
  justify-self: center;
}

.input-field input {
  background: transparent;
  border: 0;
  color: #1f2937;
  font-size: 15px;
  font-weight: 500;
  min-width: 0;
  outline: none;
  padding: 0 14px 0 0;
  width: 100%;
}

.input-field input::placeholder {
  color: #8792a7;
}

.toggle-password {
  align-items: center;
  background: transparent;
  border: 0;
  color: #4d5871;
  cursor: pointer;
  display: flex;
  height: 100%;
  justify-content: center;
  outline: none;
}

.toggle-password:focus-visible,
.forgot-link:focus-visible,
.back-login:focus-visible,
.primary-action:focus-visible {
  box-shadow: 0 0 0 4px rgba(109, 82, 207, 0.18);
}

.login-options {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
}

.remember-access {
  align-items: center;
  color: #566174;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  gap: 10px;
  margin: 0;
}

.remember-access input {
  accent-color: #5a3dad;
  height: 18px;
  width: 18px;
}

.forgot-link,
.back-login {
  background: transparent;
  border: 0;
  color: #4d2ba1;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
}

.primary-action,
.back-login {
  align-items: center;
  border-radius: 8px;
  display: flex;
  justify-content: center;
}

.primary-action {
  background: linear-gradient(135deg, #5c36bb 0%, #3f238b 100%);
  border: 0;
  box-shadow: 0 14px 24px rgba(90, 61, 173, 0.25);
  color: #ffffff;
  cursor: pointer;
  font-size: 16px;
  font-weight: 800;
  gap: 14px;
  min-height: clamp(46px, 5.8vh, 56px);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.primary-action:hover {
  box-shadow: 0 18px 30px rgba(90, 61, 173, 0.28);
  transform: translateY(-1px);
}

.primary-action:disabled {
  cursor: wait;
  opacity: 0.82;
  transform: none;
}

.primary-action:disabled:hover {
  box-shadow: 0 14px 24px rgba(90, 61, 173, 0.25);
}

.login-footer {
  align-items: center;
  display: grid;
  gap: 22px;
  grid-template-columns: clamp(110px, 9vw, 132px) 1px minmax(0, 1fr);
  margin-top: clamp(28px, 4vh, 46px);
}

.login-footer img {
  display: block;
  margin: -64px -96px -64px -78px;
  max-width: none;
  width: clamp(260px, 19vw, 318px);
}

.login-footer span {
  background: #d9d2e9;
  height: 46px;
}

.login-footer p {
  color: #667085;
  font-size: clamp(12px, 0.95vw, 14px);
  line-height: 1.45;
  margin: 0;
}

.invalid-input {
  border-color: #e74c3c !important;
  box-shadow: 0 0 0 4px rgba(231, 76, 60, 0.12) !important;
}

.invalid-message {
  color: #e74c3c;
  font-size: 13px;
  margin: 0;
  min-height: 18px;
}

.recovery-form {
  gap: 26px;
}

.back-login {
  gap: 8px;
  min-height: 38px;
}

.recovery-card {
  justify-content: center;
}

.panel-divider {
  height: 100%;
  left: 50%;
  pointer-events: none;
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  width: 1px;
  z-index: 4;
}

.panel-divider::before {
  background: rgba(130, 92, 255, 0.78);
  box-shadow:
    0 0 8px rgba(137, 95, 255, 0.68),
    0 0 18px rgba(167, 128, 255, 0.34),
    -3px 0 14px rgba(90, 61, 173, 0.22);
  content: "";
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 1px;
}

.panel-divider::after {
  background: radial-gradient(circle, rgba(137, 95, 255, 0.24), transparent 68%);
  content: "";
  height: 90px;
  left: 50%;
  opacity: 0.58;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 36px;
}

.divider-icon {
  align-items: center;
  background: linear-gradient(145deg, #6a45d4 0%, #3f238b 100%);
  border: 2px solid #ffffff;
  border-radius: 50%;
  box-shadow:
    0 10px 22px rgba(37, 17, 84, 0.24),
    0 0 18px rgba(137, 95, 255, 0.28);
  display: flex;
  height: 40px;
  justify-content: center;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  z-index: 1;
}

.divider-icon svg {
  color: #ffffff;
  display: block;
  height: 21px;
  width: 21px;
}

.divider-icon path {
  fill: currentColor;
}

.showcase-panel {
  background:
    radial-gradient(circle at 12% 45%, rgba(119, 71, 255, 0.34), transparent 28%),
    radial-gradient(circle at 86% 18%, rgba(74, 35, 160, 0.28), transparent 32%),
    linear-gradient(135deg, #271064 0%, #170941 45%, #0d062c 100%);
  color: #ffffff;
  min-height: 0;
  overflow: hidden;
  position: relative;
}

.showcase-panel::before {
  border: 1px solid rgba(148, 111, 255, 0.22);
  border-radius: 50%;
  content: "";
  height: 520px;
  position: absolute;
  right: -190px;
  top: 250px;
  transform: rotate(-12deg);
  width: 840px;
}

.showcase-panel::after {
  background:
    repeating-linear-gradient(
      150deg,
      transparent 0,
      transparent 22px,
      rgba(153, 117, 255, 0.12) 23px,
      transparent 24px
    );
  content: "";
  inset: 22% -14% 18% 15%;
  opacity: 0.7;
  position: absolute;
  transform: skewY(-8deg);
}

.showcase-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  min-height: 0;
  padding: clamp(42px, 8vh, 98px) clamp(44px, 6vw, 92px)
    clamp(24px, 5vh, 58px);
  position: relative;
  z-index: 1;
}

.product-badge {
  align-self: flex-start;
  border: 1px solid rgba(177, 146, 255, 0.55);
  border-radius: 999px;
  color: #a98cff;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0;
  padding: 8px 20px;
}

.showcase-copy {
  max-width: 540px;
}

.showcase-copy h2 {
  color: #ffffff;
  font-size: clamp(30px, 3.25vw, 40px);
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.12;
  margin: 0;
}

.showcase-copy strong {
  color: #8f67ff;
  display: inline;
  font-weight: 800;
}

.showcase-copy p {
  color: rgba(255, 255, 255, 0.86);
  font-size: clamp(15px, 1.25vw, 19px);
  line-height: 1.5;
  margin: clamp(14px, 2vh, 24px) 0 0;
  max-width: 470px;
}

.rocket-art {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: clamp(150px, 24vh, 250px);
  position: relative;
}

.rocket-art img {
  animation: floatRocket 5.6s ease-in-out infinite;
  filter:
    brightness(1.55)
    contrast(1.12)
    saturate(1.15)
    drop-shadow(0 0 22px rgba(174, 146, 255, 0.5));
  max-height: min(25vh, 230px);
  max-width: 420px;
  object-fit: contain;
  opacity: 1;
  position: relative;
  width: min(72%, 520px);
  z-index: 1;
}

.glow-orbit {
  animation: pulseGlow 4.8s ease-in-out infinite;
  background: rgba(137, 95, 255, 0.22);
  border-radius: 50%;
  filter: blur(28px);
  height: clamp(100px, 16vh, 150px);
  position: absolute;
  width: min(420px, 70%);
}

.feature-grid {
  display: grid;
  gap: 0;
  grid-template-columns: repeat(3, 1fr);
}

.feature-card {
  padding: 0 clamp(12px, 1.8vw, 22px);
  position: relative;
  text-align: center;
}

.feature-card + .feature-card {
  border-left: 1px solid rgba(255, 255, 255, 0.2);
}

.feature-card svg {
  color: #8d65ff;
  font-size: clamp(30px, 3vw, 42px);
  margin-bottom: clamp(10px, 1.6vh, 16px);
}

.feature-card h3 {
  color: #ffffff;
  font-size: clamp(14px, 1.15vw, 17px);
  font-weight: 800;
  margin: 0 0 10px;
}

.feature-card p {
  color: rgba(255, 255, 255, 0.82);
  font-size: clamp(12px, 0.95vw, 14px);
  line-height: 1.45;
  margin: 0;
}

@keyframes floatRocket {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-12px);
  }
}

@keyframes pulseGlow {
  0%,
  100% {
    opacity: 0.42;
    transform: scale(0.96);
  }

  50% {
    opacity: 0.78;
    transform: scale(1.04);
  }
}

@media screen and (max-width: 1100px) {
  .login-page {
    padding: 12px;
  }

  .login-shell {
    height: 100%;
    min-height: auto;
  }

  .login-panel {
    padding: clamp(18px, 3vh, 34px) clamp(22px, 5vw, 54px);
  }

  .showcase-content {
    padding: clamp(34px, 6vh, 64px) 8% clamp(22px, 4vh, 44px);
  }
}

@media screen and (max-width: 900px) {
  .login-shell {
    aspect-ratio: auto;
    display: block;
    max-width: 560px;
    width: 100%;
  }

  .login-panel {
    height: 100%;
    padding: clamp(18px, 4vh, 34px) 28px;
  }

  .showcase-panel,
  .panel-divider {
    display: none;
  }

  .login-form {
    justify-content: center;
  }
}

@media screen and (max-width: 520px) {
  .login-page {
    align-items: stretch;
    padding: 0;
  }

  .login-shell {
    border-radius: 0;
    box-shadow: none;
    height: 100%;
    min-height: 0;
  }

  .login-panel {
    padding: 18px;
  }

  .brand-logo {
    max-width: 280px;
  }

  .login-options {
    align-items: center;
    flex-direction: row;
    gap: 10px;
  }

  .login-footer {
    display: none;
  }

  .login-footer span {
    display: none;
  }
}

@media screen and (max-height: 780px) and (min-width: 901px) {
  .login-panel {
    padding: 22px clamp(36px, 5vw, 64px);
  }

  .login-form {
    gap: 8px;
  }

  .brand-logo {
    max-width: 260px;
  }

  .login-heading {
    gap: 4px;
  }

  .login-heading h1 {
    font-size: 18px;
  }

  .login-heading p,
  .input-field input,
  .remember-access,
  .forgot-link,
  .back-login {
    font-size: 13px;
  }

  .login-heading p {
    min-height: 40px;
  }

  .field-label {
    font-size: 13px;
  }

  .input-field {
    min-height: 42px;
  }

  .primary-action {
    font-size: 14px;
    min-height: 44px;
  }

  .login-footer {
    gap: 16px;
    margin-top: 20px;
  }

  .login-footer p {
    font-size: 11px;
  }

  .showcase-content {
    padding: 30px 42px 22px;
  }

  .product-badge {
    font-size: 12px;
    padding: 6px 16px;
  }

  .showcase-copy h2 {
    font-size: 30px;
  }

  .showcase-copy p {
    font-size: 14px;
    margin-top: 12px;
  }

  .rocket-art {
    min-height: 118px;
  }

  .rocket-art img {
    max-height: 140px;
  }

  .feature-card {
    padding: 0 12px;
  }

  .feature-card svg {
    font-size: 28px;
    margin-bottom: 8px;
  }

  .feature-card h3 {
    font-size: 13px;
    margin-bottom: 6px;
  }

  .feature-card p {
    font-size: 11px;
  }
}

@media screen and (max-height: 680px) {
  .showcase-content {
    padding-bottom: 22px;
    padding-top: 30px;
  }
}
</style>
